/**
 * Provider-mirror refresh pass for A15 / PR13.
 *
 * My Music renders Firestore first. This module then refreshes only
 * user_authorized_provider playlists whose lifecycle.refreshDueAt is due.
 * Missing OAuth is deferred — never treated as disconnect.
 */
import { collection, getDocs } from 'firebase/firestore';
import { DATA_CLASSES, retentionPolicyFor } from '../catalog/lifecycle';
import { PLAYLIST_KINDS } from '../catalog/schema';
import { db } from '../firebase';
import { recordLibraryMetric } from './libraryMetrics';
import { fetchImportedPlaylists } from './importedPlaylists';
import { isNativePlaylistId } from './playlistIdMigration';
import { SYNC_OUTCOMES, syncImportedPlaylist } from './playlistSync';
import {
  clearedProviderObservation,
  commitUserLibraryWrites,
  resolveCatalogPlaylistId,
} from './userLibrary';
import { hasYouTubeAuthorization } from './youtubeAuth';

const YOUTUBE = 'youtube';
const YOUTUBE_POLICY = retentionPolicyFor(DATA_CLASSES.USER_AUTHORIZED_PROVIDER);

/**
 * Batch size / concurrency ceiling, not a cap on how many due playlists
 * this run may process. Sequential PR9 leases avoid a quota spike.
 */
export const PROVIDER_REFRESH_BATCH_SIZE = 3;
export const PROVIDER_REFRESH_MAX_CONCURRENCY = 1;

function toMillis(value) {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.getTime();
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') {
    const converted = value.toDate();
    return converted instanceof Date && !Number.isNaN(converted.getTime())
      ? converted.getTime()
      : null;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function resolveNow(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  return new Date();
}

export function isYoutubeProviderMirror(playlist) {
  if (!playlist || typeof playlist !== 'object') return false;
  const id = playlist.userPlaylistId || playlist.playlistId;
  if (isNativePlaylistId(id) || playlist.kind === PLAYLIST_KINDS.RHYTHME_NATIVE) {
    return false;
  }
  const source = playlist.source || playlist.provider;
  if (source && source !== YOUTUBE) return false;
  if (typeof id === 'string' && id.startsWith('yt:')) return true;
  return source === YOUTUBE || !source;
}

export function isProviderMirrorRefreshDue(
  playlist,
  now = new Date(),
  policy = YOUTUBE_POLICY,
) {
  if (!isYoutubeProviderMirror(playlist)) return false;
  const current = resolveNow(now).getTime();
  const dueAt = toMillis(playlist.lifecycle?.refreshDueAt);
  if (dueAt != null) return current >= dueAt;

  const last = toMillis(
    playlist.lifecycle?.lastRefreshedAt || playlist.lifecycle?.lastFetchedAt,
  );
  if (last != null && Number.isSafeInteger(policy.refreshIntervalMs)) {
    return current >= last + policy.refreshIntervalMs;
  }
  return false;
}

export function isProviderMirrorHardExpired(
  playlist,
  now = new Date(),
  policy = YOUTUBE_POLICY,
) {
  if (!isYoutubeProviderMirror(playlist)) return false;
  const current = resolveNow(now).getTime();
  const expiresAt = toMillis(playlist.lifecycle?.expiresAt);
  if (expiresAt != null) return current >= expiresAt;
  const last = toMillis(
    playlist.lifecycle?.lastRefreshedAt || playlist.lifecycle?.lastFetchedAt,
  );
  if (last != null && Number.isSafeInteger(policy.hardExpiryMs)) {
    return current >= last + policy.hardExpiryMs;
  }
  return false;
}

function emptyMetrics() {
  return {
    operation: 'provider_refresh',
    playlistsConsidered: 0,
    playlistsDue: 0,
    playlistsDeferredNoAuth: 0,
    playlistsRefreshed: 0,
    playlistsFastPathRevalidated: 0,
    playlistsPartial: 0,
    playlistsFailed: 0,
    playlistsExpiredCleared: 0,
    providerPagesFetched: 0,
    providerItemsFetched: 0,
    firestoreBatches: 0,
    maxConcurrency: 0,
    playlistsProcessed: 0,
    drainStoppedReason: null,
    elapsedMs: 0,
  };
}

function isDrainStoppingOutcome(result) {
  const outcome = result?.outcome || result?.status;
  const code = result?.failureCode
    || result?.syncRun?.failureCode
    || result?.error?.code
    || null;
  if (outcome === SYNC_OUTCOMES.UNAUTHORIZED || code === 'unauthorized') {
    return 'unauthorized';
  }
  if (code === 'quota' || code === 'rate' || result?.error?.response?.status === 429) {
    return 'quota_or_rate';
  }
  if (outcome === SYNC_OUTCOMES.FAILED || code === 'network' || code === 'failed') {
    return 'network';
  }
  return null;
}

function recordOutcome(metrics, result) {
  const outcome = result?.outcome || result?.status;
  if (outcome === SYNC_OUTCOMES.FAST_PATH) {
    metrics.playlistsFastPathRevalidated += 1;
    metrics.playlistsRefreshed += 1;
  } else if (outcome === SYNC_OUTCOMES.COMPLETE) {
    metrics.playlistsRefreshed += 1;
  } else if (outcome === SYNC_OUTCOMES.PARTIAL) {
    metrics.playlistsPartial += 1;
  } else if (outcome === SYNC_OUTCOMES.BUSY) {
    metrics.playlistsPartial += 1;
  } else {
    metrics.playlistsFailed += 1;
  }
  metrics.providerPagesFetched += Number(result?.stats?.pagesFetched) || 0;
  metrics.providerItemsFetched += Number(result?.stats?.itemsSeen) || 0;
  metrics.firestoreBatches += Number(result?.stats?.batchCommits) || 0;
}

export async function clearExpiredPlaylistObservations(
  uid,
  playlist,
  options = {},
) {
  const firestore = options.firestore || db;
  const persistWrites = options.commitUserLibraryWrites || commitUserLibraryWrites;
  const catalogId = resolveCatalogPlaylistId(playlist);
  if (!uid || !catalogId) {
    return { batches: 0, itemsCleared: 0, assetsCleared: 0 };
  }

  const itemsRef = collection(
    firestore,
    'users',
    uid,
    'playlists',
    catalogId,
    'items',
  );
  const snapshot = await getDocs(itemsRef);
  const writes = [];
  const seenAssets = new Set();
  let itemsCleared = 0;

  (snapshot?.docs || []).forEach((entry) => {
    const data = entry.data() || {};
    writes.push({
      uid,
      path: `users/${uid}/playlists/${catalogId}/items/${entry.id}`,
      segments: ['playlists', catalogId, 'items', entry.id],
      data: {
        observation: clearedProviderObservation(data.observation, 'render_snapshot'),
      },
    });
    itemsCleared += 1;
    const assetKey = data.providerAssetKey;
    if (assetKey && !seenAssets.has(assetKey)) {
      seenAssets.add(assetKey);
    }
  });

  seenAssets.forEach((assetKey) => {
    writes.push({
      uid,
      path: `users/${uid}/assets/${assetKey}`,
      segments: ['assets', assetKey],
      data: {
        observation: clearedProviderObservation({}, 'authoritative'),
      },
    });
  });

  const batches = writes.length > 0
    ? await persistWrites(writes, { firestore })
    : 0;
  return {
    batches,
    itemsCleared,
    assetsCleared: seenAssets.size,
  };
}

/**
 * Firestore-first refresh pass. Never forces OAuth. Never blocks the caller
 * if invoked without awaiting — the function itself is async.
 */
export async function refreshDueProviderMirrors(uid, options = {}) {
  const startedMs = Date.now();
  const metrics = emptyMetrics();
  if (!uid) {
    metrics.elapsedMs = Date.now() - startedMs;
    recordLibraryMetric(metrics);
    return { ...metrics, deferred: false, outcomes: [] };
  }

  const now = resolveNow(options.now);
  const policy = options.policy || YOUTUBE_POLICY;
  const playlists = Array.isArray(options.playlists)
    ? options.playlists
    : await (options.fetchImportedPlaylists || fetchImportedPlaylists)(uid);
  const considered = playlists.filter(isYoutubeProviderMirror);
  const due = considered.filter((playlist) => (
    isProviderMirrorRefreshDue(playlist, now, policy)
  ));
  const expired = considered.filter((playlist) => (
    isProviderMirrorHardExpired(playlist, now, policy)
  ));
  metrics.playlistsConsidered = considered.length;
  metrics.playlistsDue = due.length;

  const authorized = options.hasYouTubeAuthorization
    ? options.hasYouTubeAuthorization()
    : hasYouTubeAuthorization();

  if (!authorized) {
    metrics.playlistsDeferredNoAuth = due.length;
    const outcomes = [];
    for (const playlist of expired) {
      // eslint-disable-next-line no-await-in-loop
      const cleared = await clearExpiredPlaylistObservations(uid, playlist, options);
      metrics.firestoreBatches += cleared.batches;
      metrics.playlistsExpiredCleared += 1;
      outcomes.push({
        playlistId: playlist.playlistId,
        outcome: 'expired_cleared',
      });
    }
    metrics.elapsedMs = Date.now() - startedMs;
    recordLibraryMetric(metrics);
    return { ...metrics, deferred: true, outcomes };
  }

  const syncFn = options.syncImportedPlaylist || syncImportedPlaylist;
  const batchSize = Number.isSafeInteger(options.batchSize)
    ? options.batchSize
    : PROVIDER_REFRESH_BATCH_SIZE;
  const stillAuthorized = options.hasYouTubeAuthorization
    || hasYouTubeAuthorization;
  const outcomes = [];
  let inFlight = 0;

  for (let offset = 0; offset < due.length; offset += batchSize) {
    const batch = due.slice(offset, offset + batchSize);
    for (const playlist of batch) {
      if (!stillAuthorized()) {
        metrics.drainStoppedReason = 'unauthorized';
        break;
      }
      inFlight += 1;
      metrics.maxConcurrency = Math.max(metrics.maxConcurrency, inFlight);
      let result;
      try {
        // Sequential: reuse PR9 leases; never launch the whole due set at once.
        // eslint-disable-next-line no-await-in-loop
        result = await syncFn(uid, playlist, {
          firestore: options.firestore,
          now,
          ...(options.syncOptions || {}),
        });
      } catch (error) {
        result = {
          outcome: SYNC_OUTCOMES.FAILED,
          failureCode: error?.code === 'quota' || error?.code === 'rate'
            ? error.code
            : 'network',
          error,
        };
      } finally {
        inFlight -= 1;
      }
      metrics.playlistsProcessed += 1;
      recordOutcome(metrics, result);
      outcomes.push({
        playlistId: playlist.playlistId,
        outcome: result?.outcome || result?.status,
      });
      const stopReason = isDrainStoppingOutcome(result);
      if (stopReason) {
        metrics.drainStoppedReason = stopReason;
        break;
      }
    }
    if (metrics.drainStoppedReason) break;
    if (typeof options.yieldBetweenBatches === 'function') {
      // eslint-disable-next-line no-await-in-loop
      await options.yieldBetweenBatches();
    }
  }

  metrics.elapsedMs = Date.now() - startedMs;
  recordLibraryMetric(metrics);
  return { ...metrics, deferred: false, outcomes };
}
